import React from 'react';
import { Button } from 'antd';
import { PlusOutlined, CheckOutlined } from '@ant-design/icons';
import { ScreenQuestionProps } from './types';

type SelectableTagListProps = {
  items: ScreenQuestionProps[];
  selectedKeys: string[];
  onChange: (
    nextSelectedKeys: string[],
    meta: { tag: ScreenQuestionProps; selected: boolean },
  ) => void;
  onClick?: (tag: ScreenQuestionProps, selected: boolean) => void;
  getKey?: (tag: ScreenQuestionProps) => string; // override if titles can repeat
  className?: string;
  size?: 'small' | 'middle' | 'large';
  // Additional active highlight keys that should appear selected (highlighted) without being in selectedKeys
  activeKeys?: string[];
  // Keys that should always show a plus icon even if highlighted/active
  forcePlusKeys?: string[];
};

const titleKey = (t: ScreenQuestionProps) =>
  t.title?.toString().trim().toLowerCase().replace(/\s+/g, '-');

export const SelectableTagList: React.FC<SelectableTagListProps> = ({
  items,
  selectedKeys,
  onChange,
  onClick,
  getKey = titleKey,
  className = '',
  size = 'middle',
  activeKeys = [],
  forcePlusKeys = [],
}) => {
  const selectedSet = React.useMemo(() => new Set(selectedKeys), [selectedKeys]);
  const activeSet = React.useMemo(() => new Set(activeKeys), [activeKeys]);
  const forcePlusSet = React.useMemo(() => new Set(forcePlusKeys), [forcePlusKeys]);

  const handleToggle = (tag: ScreenQuestionProps) => {
    const key = getKey(tag);
    const wasSelected = selectedSet.has(key);
    const willSelect = !wasSelected;

    const next = wasSelected ? selectedKeys.filter((k) => k !== key) : [...selectedKeys, key];

    console.log('[Tag Click]', {
      key,
      title: tag.title,
      type: tag.type,
      category: tag.category,
      selected: willSelect,
    });

    onClick?.(tag, willSelect);
    onChange(next, { tag, selected: willSelect });
  };

  return (
    <div className={`flex flex-wrap gap-3 mt-3 ${className}`}>
      {items.map((tag) => {
        const key = getKey(tag);
        const isSelected = selectedSet.has(key);
        const isActive = isSelected || activeSet.has(key);
        const showPlus = forcePlusSet.has(key) || !isActive;

        return (
          <Button
            key={key}
            size={size}
            shape="round"
            aria-pressed={isActive}
            onClick={() => handleToggle(tag)}
            icon={showPlus ? <PlusOutlined /> : <CheckOutlined />}
            className={[
              'inline-flex items-center font-semibold gap-2 border transition !hover:text-[#6941C6]',
              isActive
                ? '!text-[#6941C6] !bg-[#F9F5FF] border-[#6941C670]'
                : 'bg-white border-gray-300 text-gray-800 hover:bg-gray-50',
            ].join(' ')}>
            <span className="whitespace-nowrap">{tag.title}</span>
          </Button>
        );
      })}
    </div>
  );
};
